import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, Loader2, Eye, EyeOff, UserCircle2, Shield } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// -----------------------------------------------------------------------------
// Login — demo gate. Shown before the PersonaPicker landing page.
// Single shared credential is defined in backend/.env (DEMO_EMAIL /
// DEMO_PASSWORD) and surfaced to the presenter as a muted hint on the right
// panel so they never have to context-switch during a pitch.
// -----------------------------------------------------------------------------

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { brand } = useBranding();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // After login, admin lands straight on /admin/cockpit while user lands on
  // the persona picker. If the user was bounced here mid-navigation,
  // honour the original destination (only for user role — admin always
  // starts at the cockpit so the demo opens cleanly).
  const from = location.state?.from || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user"); // user | admin
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const brandBlue = brand?.primary || "#0033A0";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Enter both email and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      localStorage.setItem("flexspace_role_choice", role);
      if (role === "admin") {
        // Admin logins skip the persona picker entirely: seed a sensible
        // default persona (Executive) + flip viewMode to admin so AppLayout
        // renders the admin IA, and route straight into the Cockpit.
        localStorage.setItem("flexspace.viewMode", "admin");
        localStorage.setItem(
          "flexspace.persona",
          JSON.stringify({ id: "executive", name: "Executive", title: "Business Leader", icon: "Briefcase" }),
        );
        localStorage.setItem("flexspace.userName", "Flexspace Admin");
        localStorage.setItem("flexspace.userEmail", "manchit.kalani@gmail.com");
        window.location.replace("/admin/cockpit");
      } else {
        localStorage.setItem("flexspace.viewMode", "user");
        navigate(from && from !== "/login" ? from : "/", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50" data-testid="login-page">
      {/* Left · brand panel */}
      <div
        className="hidden lg:flex lg:w-5/12 relative overflow-hidden items-center justify-center p-12"
        style={{ background: `linear-gradient(135deg, ${brandBlue} 0%, #0F172A 100%)` }}
      >
        <div className="relative z-10 max-w-md">
          <div
            className="w-14 h-14 rounded-2xl grid place-items-center bg-white/10 mb-6 text-2xl font-bold text-white backdrop-blur"
            style={{ border: "1px solid rgba(255,255,255,0.2)" }}
          >
            {brand?.logoLetter || "F"}
          </div>
          <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/60 mb-3">
            {brand?.name || "Flexspace"} · Device Lifecycle Intelligence
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-[1.08] mb-4 tracking-tight">
            From fragmented device lifecycle
            <br />
            <span className="text-white/70">to an intelligent closed loop.</span>
          </h1>
          <h2 className="text-xl lg:text-2xl font-semibold text-white/90 leading-snug mb-5 tracking-tight">
            Hire. Equip. Operate. Recover.
          </h2>
          <p className="text-white/70 text-[13.5px] leading-relaxed">
            Workday <span className="opacity-60">·</span> ServiceNow <span className="opacity-60">·</span> Ariba <span className="opacity-60">·</span> OEMs <span className="opacity-60">·</span> Intune / Jamf <span className="opacity-60">·</span> Nexthink <span className="opacity-60">·</span> Blancco — one source of truth, continuously reconciled.
          </p>
        </div>
        {/* soft orbs */}
        <div className="absolute -bottom-24 -right-20 w-80 h-80 rounded-full" style={{ background: `radial-gradient(circle, ${brandBlue}66, transparent 65%)` }} />
        <div className="absolute -top-20 -left-10 w-60 h-60 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.08), transparent 65%)" }} />
      </div>

      {/* Right · form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div
              className="w-10 h-10 rounded-xl grid place-items-center font-bold text-white"
              style={{ backgroundColor: brandBlue }}
            >
              {brand?.logoLetter || "F"}
            </div>
            <div className="text-sm font-bold text-slate-900">{brand?.name || "Flexspace"}</div>
          </div>

          <div className="text-[10px] tracking-[0.14em] uppercase font-bold mb-2" style={{ color: brandBlue }}>
            Sign in
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Welcome back.</h2>
          <p className="text-sm text-slate-500 mb-7">
            Pick your view and sign in to open the console.
          </p>

          <form onSubmit={submit} className="space-y-4" noValidate>
            {/* Role selector · drives post-login destination */}
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">
                Sign in as
              </Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Role">
                {[
                  { id: "user",  label: "User",  Icon: UserCircle2, caption: "Catalogue · Orders · MyPC" },
                  { id: "admin", label: "Admin", Icon: Shield,      caption: "Cockpit · Lifecycle · Studio" },
                ].map((r) => {
                  const active = role === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setRole(r.id)}
                      data-testid={`login-role-${r.id}`}
                      className="rounded-lg px-3 py-2.5 text-left transition-all"
                      style={active
                        ? { backgroundColor: brandBlue, color: "#fff", border: `1px solid ${brandBlue}` }
                        : { backgroundColor: "#fff", color: "#334155", border: "1px solid #E2E8F0" }}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <r.Icon size={14} />
                        {r.label}
                      </div>
                      <div className="text-[10.5px] mt-0.5" style={{ color: active ? "rgba(255,255,255,0.85)" : "#64748B" }}>
                        {r.caption}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">
                Email
              </Label>
              <div className="relative mt-1.5">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9 h-11"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-11"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="rounded-md px-3 py-2 text-[12px] font-semibold bg-red-50 text-red-700"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 text-white font-semibold hover:opacity-90"
              style={{ backgroundColor: brandBlue }}
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Signing in…
                </>
              ) : (
                `Sign in${role === "admin" ? " · Admin" : ""}`
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
